import { useEffect, useState } from 'react';
import {
  Drawer,
  Button,
  Typography,
  Empty,
  Spin,
  Alert,
  Tag,
  Tooltip,
  Skeleton,
} from 'antd';
import {
  HistoryOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  RobotOutlined,
  EditOutlined,
  ImportOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  listHistory,
  getHistoryDetail,
  CHANGE_SOURCE_LABEL,
  type PrebuiltPromptHistoryRow,
  type PrebuiltPromptHistoryDetail,
  type PrebuiltPromptChangeSource,
} from '@/api/prebuiltPromptHistory';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// PrebuiltPromptHistoryDrawer — version history for ai.PrebuiltPrompt.Instructions
// ─────────────────────────────────────────────────────────────────────────────
//
// Reads ai.PrebuiltPromptHistory via /api/PrebuiltPrompt/History/{promptId}.
// Mirrors the InstructionHistoryDrawer pattern (timeline of cards, plain
// English copy, side-by-side diff) but tuned to the simpler use case: no
// restore button (V1 — every change is auditable but not yet revertable
// from the UI), and a ChangeSource badge so operators can spot whether a
// row came from a manual edit or a Review-with-AI Accept.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  promptId: number | null;
  /** Optional — surfaces in the drawer header so the user knows what they're auditing. */
  promptLabel?: string | null;
  onClose: () => void;
}

type Pane = 'list' | 'detail';

export function PrebuiltPromptHistoryDrawer({
  open,
  promptId,
  promptLabel,
  onClose,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [pane, setPane] = useState<Pane>('list');
  const [rows, setRows] = useState<PrebuiltPromptHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [detail, setDetail] = useState<PrebuiltPromptHistoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Load the timeline whenever the drawer opens against a new prompt.
  useEffect(() => {
    if (!open || !promptId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setPane('list');
    setDetail(null);
    void (async () => {
      try {
        const data = await listHistory(promptId);
        if (cancelled) return;
        setRows(data);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, promptId]);

  function openDetail(row: PrebuiltPromptHistoryRow) {
    if (!promptId) return;
    setPane('detail');
    setDetail(null);
    setDetailLoading(true);
    void (async () => {
      try {
        const d = await getHistoryDetail(promptId, row.id);
        setDetail(d);
      } finally {
        setDetailLoading(false);
      }
    })();
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="80%"
      closable={false}
      destroyOnClose
      styles={{
        body: { padding: 0, background: isDark ? colors.slate[800] : '#FFFFFF' },
        header: { display: 'none' },
      }}
    >
      <DrawerHeader
        pane={pane}
        promptLabel={promptLabel}
        rowCount={rows.length}
        onBack={() => setPane('list')}
        onClose={onClose}
        isDark={isDark}
      />

      <div style={{ padding: '16px 24px 32px 24px' }}>
        {pane === 'list' ? (
          <ListPane
            loading={loading}
            error={loadError}
            rows={rows}
            onOpenDetail={openDetail}
            isDark={isDark}
          />
        ) : (
          <DetailPane loading={detailLoading} detail={detail} isDark={isDark} />
        )}
      </div>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function DrawerHeader({
  pane,
  promptLabel,
  rowCount,
  onBack,
  onClose,
  isDark,
}: {
  pane: Pane;
  promptLabel?: string | null;
  rowCount: number;
  onBack: () => void;
  onClose: () => void;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '14px 24px',
        background: isDark ? colors.slate[800] : '#FFFFFF',
        borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
      }}
    >
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Text
          type="secondary"
          style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
        >
          {promptLabel || 'Prebuilt prompt'}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {pane === 'detail' && (
            <Button size="small" type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
              Back to timeline
            </Button>
          )}
          <Title level={4} style={{ margin: 0, color: isDark ? colors.slate[50] : colors.slate[900] }}>
            <HistoryOutlined style={{ marginRight: 8, color: colors.violet[600] }} />
            {pane === 'list' ? 'Version history' : 'Version detail'}
          </Title>
          {pane === 'list' && rowCount > 0 && (
            <Tag style={{ marginLeft: 4 }}>{rowCount.toLocaleString()} versions</Tag>
          )}
        </div>
      </div>
      <Tooltip title="Close (Esc)">
        <Button type="text" size="large" icon={<CloseOutlined />} onClick={onClose} />
      </Tooltip>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ListPane({
  loading,
  error,
  rows,
  onOpenDetail,
  isDark,
}: {
  loading: boolean;
  error: string | null;
  rows: PrebuiltPromptHistoryRow[];
  onOpenDetail: (row: PrebuiltPromptHistoryRow) => void;
  isDark: boolean;
}) {
  if (loading) {
    return (
      <div style={{ padding: '40px 0' }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }
  if (error) {
    return <Alert type="error" message="Couldn't load history" description={error} showIcon />;
  }
  if (rows.length === 0) {
    return (
      <Empty
        description={
          <span>
            <Text strong>No versions yet</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              History captures every change to this prompt's body — from a manual edit
              or from Review-with-AI Accept. Once an edit happens, the timeline appears here.
            </Text>
          </span>
        }
        style={{ padding: '50px 0' }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((row, i) => (
        <TimelineCard
          key={row.id}
          row={row}
          isFirst={i === 0}
          onOpen={() => onOpenDetail(row)}
          isDark={isDark}
        />
      ))}
    </div>
  );
}

function TimelineCard({
  row,
  isFirst,
  onOpen,
  isDark,
}: {
  row: PrebuiltPromptHistoryRow;
  isFirst: boolean;
  onOpen: () => void;
  isDark: boolean;
}) {
  const ts = dayjs(row.changedAtUtc);
  const delta = row.newLength - row.previousLength;
  const palette = sourcePalette(row.changeSource, isDark);

  return (
    <button
      onClick={onOpen}
      style={{
        textAlign: 'left',
        cursor: 'pointer',
        padding: '14px 16px',
        borderRadius: 10,
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.violet[500];
        e.currentTarget.style.boxShadow = '0 1px 6px rgba(124, 58, 237, 0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isDark ? colors.slate[700] : colors.slate[200];
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Tag
          icon={palette.icon}
          style={{
            background: palette.fill,
            color: palette.text,
            border: `1px solid ${palette.border}`,
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {CHANGE_SOURCE_LABEL[row.changeSource]}
        </Tag>
        {isFirst && (
          <Tag color="success" style={{ fontWeight: 600, fontSize: 11 }}>
            Latest
          </Tag>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {ts.fromNow()} · {ts.format('MMM D, YYYY h:mm A')}
        </Text>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          flexWrap: 'wrap',
          color: isDark ? colors.slate[200] : colors.slate[700],
        }}
      >
        <Text strong style={{ color: isDark ? colors.slate[50] : colors.slate[900] }}>
          {row.changedByName || `User #${row.changedBy}`}
        </Text>
        <DeltaBadge delta={delta} isDark={isDark} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {row.previousLength.toLocaleString()} → {row.newLength.toLocaleString()} chars
        </Text>
      </div>
      {row.note && (
        <Text
          type="secondary"
          style={{
            fontSize: 12,
            fontStyle: 'italic',
            background: isDark ? colors.slate[800] : colors.slate[50],
            padding: '6px 10px',
            borderRadius: 6,
          }}
        >
          “{row.note}”
        </Text>
      )}
    </button>
  );
}

function DeltaBadge({ delta, isDark }: { delta: number; isDark: boolean }) {
  if (delta === 0) {
    return (
      <Text type="secondary" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
        no length change
      </Text>
    );
  }
  const positive = delta > 0;
  const sign = positive ? '+' : '−';
  return (
    <span
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12,
        fontWeight: 700,
        padding: '1px 7px',
        borderRadius: 999,
        background: positive
          ? (isDark ? colors.green[700] + '55' : colors.green[100])
          : (isDark ? colors.red[700] + '55' : colors.red[100]),
        color: positive
          ? (isDark ? colors.green[100] : colors.green[700])
          : (isDark ? colors.red[100] : colors.red[700]),
      }}
    >
      {sign}{Math.abs(delta).toLocaleString()} chars
    </span>
  );
}

function sourcePalette(
  source: PrebuiltPromptChangeSource,
  isDark: boolean,
): { icon: React.ReactNode; fill: string; text: string; border: string } {
  switch (source) {
    case 'review_accept':
      return {
        icon: <RobotOutlined />,
        fill: isDark ? colors.violet[700] + '55' : colors.violet[100],
        text: isDark ? colors.violet[100] : colors.violet[700],
        border: isDark ? colors.violet[700] : colors.violet[200],
      };
    case 'review_edit':
      return {
        icon: <RobotOutlined />,
        fill: isDark ? colors.amber[700] + '55' : colors.amber[100],
        text: isDark ? colors.amber[100] : colors.amber[700],
        border: isDark ? colors.amber[700] : colors.amber[100],
      };
    case 'import':
      return {
        icon: <ImportOutlined />,
        fill: isDark ? colors.slate[700] : colors.slate[100],
        text: isDark ? colors.slate[100] : colors.slate[700],
        border: isDark ? colors.slate[600] : colors.slate[200],
      };
    case 'manual_edit':
    default:
      return {
        icon: <EditOutlined />,
        fill: isDark ? colors.ink[700] + '55' : colors.ink[100],
        text: isDark ? colors.ink[200] : colors.ink[700],
        border: isDark ? colors.ink[600] : colors.ink[200],
      };
  }
}

// ─────────────────────────────────────────────────────────────────────────────

function DetailPane({
  loading,
  detail,
  isDark,
}: {
  loading: boolean;
  detail: PrebuiltPromptHistoryDetail | null;
  isDark: boolean;
}) {
  if (loading) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!detail) {
    return (
      <Alert
        type="warning"
        message="Couldn't load this version"
        description="The row may have been removed or doesn't belong to this prompt."
        showIcon
      />
    );
  }

  const ts = dayjs(detail.changedAtUtc);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          padding: '12px 14px',
          borderRadius: 10,
          background: isDark ? colors.slate[900] : colors.slate[50],
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Tag>{CHANGE_SOURCE_LABEL[detail.changeSource]}</Tag>
        <Text strong>{detail.changedByName || `User #${detail.changedBy}`}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {ts.fromNow()} · {ts.format('MMM D, YYYY h:mm A')}
        </Text>
        {detail.note && (
          <Text
            type="secondary"
            style={{ fontSize: 12, fontStyle: 'italic', marginLeft: 'auto' }}
          >
            “{detail.note}”
          </Text>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
        }}
      >
        <SidePanel
          label="Before"
          accent={colors.red[600]}
          chars={detail.previousLength}
          body={detail.previousInstructions}
          isDark={isDark}
        />
        <SidePanel
          label="After"
          accent={colors.green[600]}
          chars={detail.newLength}
          body={detail.newInstructions}
          isDark={isDark}
        />
      </div>
    </div>
  );
}

function SidePanel({
  label,
  accent,
  chars,
  body,
  isDark,
}: {
  label: string;
  accent: string;
  chars: number;
  body: string | null;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderTop: `3px solid ${accent}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: isDark ? colors.slate[800] : colors.slate[50],
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <Text strong style={{ color: accent }}>
          {label}
        </Text>
        <Text type="secondary" style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
          {chars.toLocaleString()} chars
        </Text>
      </div>
      <pre
        style={{
          margin: 0,
          padding: 12,
          maxHeight: 540,
          overflow: 'auto',
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: isDark ? colors.slate[100] : colors.slate[800],
          background: 'transparent',
        }}
      >
        {body && body.length > 0 ? body : '(empty)'}
      </pre>
    </div>
  );
}
